import {PageHeader} from '../ui/PageHeader';
import {ResponsiveTable} from '../ui/ResponsiveTable';
import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import { Link } from "../ui/ScopedLink";
import { useStaffSession } from "../ui/StaffSession";
import { ErrorState } from "../ui/State";
import {XeroCashGroups} from "./XeroCashGroups";
import {XeroSupplierSourceCash} from "./XeroSupplierSourceCash";
import {XeroPayableNetting} from "./XeroPayableNetting";
import {XeroSupplierCash} from "./XeroSupplierCash";
import {originalXeroEvidence} from "./xero-history-import-types";
import type {Invoice, CreditApplication, ReceivableMapping, PayableMapping, Payment, PaymentMapping, StructureMapping, StructureResult, SourceEvidence, OriginalEventMapping, CustomerCashMapping, Packet, Evidence, Result} from "./xero-history-import-types";
export function XeroHistoryImport() {
  const scope = useStaffSession()?.effectiveScope;
  return scope?.kind === "building" ? (
    <BuildingImport key={scope.id} building={scope.id} />
  ) : (
    <PageHeader title="Historic Xero import" context="Choose a managed building using the workspace building switcher."/>
  );
}
function BuildingImport({ building }: { building: string }) {
  const [packet, setPacket] = useState<Packet>(),
    [documents, setDocuments] = useState<Evidence[]>([]),
    [results, setResults] = useState<Record<string, Result>>({}),
    [creditResults, setCreditResults] = useState<Record<string, Result>>({}),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false),
    [page, setPage] = useState(0),
    [creditPage, setCreditPage] = useState(0),
    [reason, setReason] = useState("Copy reviewed original Xero accounting history");
  const [payableResults, setPayableResults] = useState<Record<string, Result>>({});
  const [paymentResults, setPaymentResults] = useState<Record<string, Result>>({}),
    [paymentPage, setPaymentPage] = useState(0);
  const [structureResult, setStructureResult] = useState<StructureResult>();
  const [eventResults,setEventResults]=useState<Record<string,Result>>({}),[eventPage,setEventPage]=useState(0);
  const [customerCashResults,setCustomerCashResults]=useState<Record<string,Result>>({}),
    [overpaymentResults,setOverpaymentResults]=useState<Record<string,Result>>({}),
    [customerCashPage,setCustomerCashPage]=useState(0);
  const stop = useRef(false);
  useEffect(
    () => () => {
      stop.current = true;
    },
    [],
  );
  const [receivableResults, setReceivableResults] = useState<Record<string, Result>>({}),
    [receivablePage, setReceivablePage] = useState(0);
  const root = `/api/finance/accounting/${building}/xero-history`,
    invoices =
      packet?.history.invoices.filter(
        (i) => ["Bill", "Bill credit note"].includes(i.kind) && !["Draft", "Submitted", "Awaiting Approval", "Voided", "Deleted"].includes(i.status),
      ) ?? [];
  const retainedEvidence = (entry: SourceEvidence | undefined) => {
    const hashes = entry?.sources.filter((s) => !entry.selectedChecksum || s.checksum === entry.selectedChecksum).map((s) => s.checksum) ?? [];
    return documents.filter((d) => hashes.includes(d.checksum));
  };
  const evidenceFor = (id: string) => retainedEvidence(packet?.invoiceEvidence.find((i) => i.sourceId === id));
  async function load(file: File) {
    setError(undefined);
    setBusy(true);
    setPacket(undefined);
    setStructureResult(undefined);
    setResults({});
    setPayableResults({});
    setEventResults({});setEventPage(0);
    setPaymentResults({});
    setPaymentPage(0);
    setCreditResults({});
    setCustomerCashResults({});setOverpaymentResults({});setCustomerCashPage(0);
    setReceivableResults({});
    setPage(0);
    setCreditPage(0);
    setReceivablePage(0);
    try {
      if (file.size > 30_000_000) throw Error("Choose an import preparation file smaller than 30 MB.");
      const data = JSON.parse(await file.text());
      if (data.format !== "xero-history-preparation-v1" || !Array.isArray(data.organisations)) throw Error("Choose the verified Xero history preparation file.");
      const selected = data.organisations.find((o: any) => o.buildingId === building),
        p = selected?.importPacket;
      if (!p) {
        if (selected?.sourceEvents === 0) throw Error("No accounting events were retained for this building. Review its source coverage before declaring the import complete.");
        throw Error("This file has no import packet for the selected building.");
      }
      if (p.history?.buildingId !== building || !Array.isArray(p.invoiceEvidence) || !Array.isArray(p.history.invoices))
        throw Error("The packet does not match the selected building.");
      // Server validates the allowlist, original identities and complete source controls.
      await api(root + "/preview", { method: "POST", body: JSON.stringify(p.history) });
      const checksums = [
        ...new Set<string>([
          ...[...p.invoiceEvidence, ...(p.paymentEvidence ?? [])].flatMap((i: any) => i.sources.map((s: any) => s.checksum)),
          ...(p.structureMapping?.sourceChecksums ?? []),
          ...(p.customerCashMappings ?? []).flatMap((m:CustomerCashMapping)=>[m.sourceChecksum,m.bankWitness.checksum,...m.originalApplications.map(a=>a.sourceChecksum)]),
          ...(p.originalEventMappings??[]).flatMap((m:OriginalEventMapping)=>[m.sourceChecksum,...m.bankLinks.map(l=>l.witness.checksum),...(m.sourceOnlyBanks??[]).map(b=>b.historyChecksum)]),
          ...(p.paymentMappings??[]).flatMap((m:PaymentMapping)=>m.bankWitness?[m.bankWitness.checksum]:[]),
          ...(p.cashGroupMappings??[]).flatMap((g:import("./xero-history-import-types").CashGroupMapping)=>[...g.payments.map(s=>s.sourceChecksum),...g.fees.map(s=>s.sourceChecksum),...g.bankWitnesses.map(w=>w.checksum)]),
          ...(p.supplierSourceCashMappings??[]).flatMap((m:import("./xero-history-import-types").SupplierSourceCashMapping)=>[m.bankWitness.checksum,...m.payments.map(s=>s.sourceChecksum)]),
          ...(p.payableNettingMappings??[]).flatMap((m:import("./xero-history-import-types").PayableNettingMapping)=>[m.bankWitness.checksum,m.adjustment.sourceChecksum,...m.payments.map(s=>s.sourceChecksum)]),
          ...(p.supplierCashMappings??[]).flatMap((m:import("./xero-history-import-types").SupplierCashMapping)=>[m.sourceChecksum,m.bankWitness.checksum]),
        ]),
      ];
      const found = await api<{ documents: Evidence[] }>(root + "/evidence", { method: "POST", body: JSON.stringify({ checksums }) });
      setDocuments(found.documents);
      setPacket(p);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  async function prepareStructure() {
    if (!packet?.structureMapping) return;
    setBusy(true);
    setError(undefined);
    try {
      const { sourceChecksums, ...mapping } = packet.structureMapping,
        evidence = sourceChecksums.map((checksum) => {
          const matches = documents.filter((d) => d.checksum === checksum);
          if (matches.length !== 1) throw Error("Each original ledger page needs one retained, scanned source document.");
          const { title: _, ...source } = matches[0]!;
          return source;
        });
      const saved = await api<StructureResult>(root + "/structure", { method: "POST", body: JSON.stringify({ ...mapping, history: packet.history, evidence, reason }) });
      const accountFor = (id: string) => saved.accounts.find((a) => a.sourceIds.includes(id))?.accountId;
      const originalFunds = (id: string, lines: ReceivableMapping["lineMappings"]) => lines.map((line) => {
        if (line.fund !== "Reserve" || line.reserveFundId) return line;
        const name = packet.history.events.find((e) => e.sourceId === id)?.lines[line.lineIndex]?.accountName.trim();
        const funds = saved.funds?.filter((f) => name && f.sourceAccounts.includes(name)) ?? [];
        return funds.length === 1 ? { ...line, reserveFundId: funds[0]!.id } : line;
      });
      const periodFor = (date: string) => {
        const periods = saved.periods.filter((p) => p.startsOn <= date && p.endsOn >= date);
        return periods.length === 1 ? periods[0]!.id : undefined;
      };
      setPacket({
        ...packet,
        originalEventMappings:(packet.originalEventMappings??[]).map(m=>({...m,lineMappings:originalFunds(m.sourceId,m.lineMappings)})),
        customerCashMappings: (packet.customerCashMappings ?? []).map(m=>({ ...m,
          accountId: saved.accounts.find(a=>a.premisesId===m.premisesId)?.accountId,
          periodId: periodFor(m.originalDate),
        })),
        receivableMappings: (packet.receivableMappings ?? []).map((m) => ({
          ...m,
          accountId: accountFor(m.sourceId) ?? m.accountId,
          lineMappings: originalFunds(m.sourceId, m.lineMappings),
          periodId: periodFor(packet.history.invoices.find((i) => i.sourceId === m.sourceId)?.date ?? "") ?? m.periodId,
        })),
        payableMappings: (packet.payableMappings ?? []).map((m) => ({
          ...m,
          lineMappings: originalFunds(m.sourceId, m.lineMappings),
          periodId: periodFor(packet.history.invoices.find((i) => i.sourceId === m.sourceId)?.date ?? "") ?? m.periodId,
        })),
        paymentMappings: (packet.paymentMappings ?? []).map((m) => {
          const cash = packet.history.payments?.find((c) => c.sourceId === m.sourceId),
            mapped = cash?.applications.map((a) => accountFor(a.invoiceSourceId)) ?? [],
            accounts = [...new Set(mapped)].filter((id): id is string => typeof id === "string"),
            period = cash ? periodFor(cash.date) : undefined;
          return { ...m, ...(mapped.length && mapped.every(Boolean) && accounts.length === 1 ? { accountId: accounts[0]! } : {}), ...(period ? { periodId: period } : {}) };
        }),
      });
      setStructureResult(saved);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  async function prepare() {
    if (!packet) return;
    setBusy(true);
    setError(undefined);
    stop.current = false;
    try {
      for (const invoice of invoices) {
        if (stop.current) break;
        if (results[invoice.sourceId]?.canonicalId) continue;
        const matches = evidenceFor(invoice.sourceId);
        if (matches.length !== 1) {
          setResults((r) => ({
            ...r,
            [invoice.sourceId]: {
              review: matches.length
                ? "More than one retained source document matches. Resolve the source evidence before importing."
                : "Retain and scan the original export in this building first.",
            },
          }));
          continue;
        }
        try {
          const { title: _, ...evidence } = matches[0]!,
            result = await api<{ canonicalId: string }>(root + "/payables", {
              method: "POST",
              body: JSON.stringify({
                history: {
                  organisation: packet.history.organisation,
                  selector: packet.history.selector,
                  buildingId: building,
                  events: packet.history.events.filter((e) => e.sourceId === invoice.sourceId),
                  invoices: [invoice],
                },
                sourceId: invoice.sourceId,
                evidence,
                supplierId: null,
                reason,
              }),
            });
          setResults((r) => ({ ...r, [invoice.sourceId]: result }));
        } catch (e) {
          setResults((r) => ({ ...r, [invoice.sourceId]: { error: e } }));
        }
      }
    } finally {
      setBusy(false);
    }
  }
  const payableMappingFor = (id: string) => {
    const found = packet?.payableMappings?.filter((m) => m.sourceId === id) ?? [];
    return found.length === 1 ? found[0] : undefined;
  };
  async function postPayables(originalCopy=false) {
    if (!packet) return;
    setBusy(true);
    setError(undefined);
    stop.current = false;
    try {
      for (const invoice of invoices) {
        if (stop.current) break;
        if (payableResults[invoice.sourceId]?.canonicalId) continue;
        const mapping = payableMappingFor(invoice.sourceId);
        if (!mapping) {
          setPayableResults((r) => ({ ...r, [invoice.sourceId]: { review: "Review the original period and each accounting line before posting." } }));
          continue;
        }
        try {
          if(originalCopy&&!structureResult)throw Error("Prepare the original schedule and accounting periods before copying supplier accounting.");
          const periods=structureResult?.periods.filter(p=>p.startsOn<=invoice.date&&p.endsOn>=invoice.date)??[];
          if(originalCopy&&periods.length!==1)throw Error("The original invoice needs one saved accounting period covering its date.");
          const result = await api<{ canonicalId: string }>(root + (originalCopy?"/payable-source-copies":"/payable-postings"), {
            method: "POST",
            body: JSON.stringify({
              ...mapping,
              ...(originalCopy?{scheduleId:structureResult!.scheduleId,periodId:periods[0]!.id}:{}),
              reason,
              history: {
                organisation: packet.history.organisation,
                selector: packet.history.selector,
                buildingId: building,
                events: packet.history.events.filter((e) => e.sourceId === invoice.sourceId),
                invoices: [invoice],
              },
            }),
          });
          setPayableResults((r) => ({ ...r, [invoice.sourceId]: result }));
        } catch (e) {
          setPayableResults((r) => ({ ...r, [invoice.sourceId]: { error: e } }));
        }
      }
    } finally {
      setBusy(false);
    }
  }
  const pages = Math.max(1, Math.ceil(invoices.length / 25)),
    prepared = Object.values(results).filter((r) => r.canonicalId).length;
  const receivables =
    packet?.history.invoices.filter(
      (i) => ["Sales invoice", "Sales credit note"].includes(i.kind) && !["Draft", "Submitted", "Awaiting Approval", "Voided", "Deleted"].includes(i.status),
    ) ?? [];
  const receivablePages = Math.max(1, Math.ceil(receivables.length / 25)),
    postedReceivables = Object.values(receivableResults).filter((r) => r.canonicalId).length;
  const mappingFor = (id: string) => {
    const found = packet?.receivableMappings?.filter((m) => m.sourceId === id) ?? [];
    return found.length === 1 ? found[0] : undefined;
  };
  async function importReceivables() {
    if (!packet) return;
    setBusy(true);
    setError(undefined);
    stop.current = false;
    try {
      for (const invoice of receivables) {
        if (stop.current) break;
        if (receivableResults[invoice.sourceId]?.canonicalId) continue;
        const mapping = mappingFor(invoice.sourceId),
          matches = evidenceFor(invoice.sourceId);
        if (!mapping || matches.length !== 1) {
          setReceivableResults((r) => ({
            ...r,
            [invoice.sourceId]: { review: "Review the original customer account, period, accounting lines and retained source evidence first." },
          }));
          continue;
        }
        try {
          const { title: _, ...evidence } = matches[0]!,
            result = await api<{ canonicalId: string }>(root + "/receivables", {
              method: "POST",
              body: JSON.stringify({
                ...mapping,
                evidence,
                reason,
                history: {
                  organisation: packet.history.organisation,
                  selector: packet.history.selector,
                  buildingId: building,
                  events: packet.history.events.filter((e) => e.sourceId === invoice.sourceId),
                  invoices: [invoice],
                },
              }),
            });
          setReceivableResults((r) => ({ ...r, [invoice.sourceId]: result }));
        } catch (e) {
          setReceivableResults((r) => ({ ...r, [invoice.sourceId]: { error: e } }));
        }
      }
    } finally {
      setBusy(false);
    }
  }
  const payments = packet?.history.payments ?? [],
    paymentPages = Math.max(1, Math.ceil(payments.length / 25));
  const paymentEvidenceFor = (id: string) => retainedEvidence(packet?.paymentEvidence?.find((e) => e.sourceId === id));
  const paymentMappingFor = (id: string) => {
    const found = packet?.paymentMappings?.filter((m) => m.sourceId === id) ?? [];
    return found.length === 1 ? found[0] : undefined;
  };
  async function applyPayments() {
    if (!packet) return;
    setBusy(true);
    setError(undefined);
    stop.current = false;
    try {
      for (const cash of payments) {
        if (stop.current) break;
        if (paymentResults[cash.sourceId]?.canonicalId) continue;
        const matches = paymentEvidenceFor(cash.sourceId),
          mapping = paymentMappingFor(cash.sourceId),
          event = packet.history.events.find((e) => e.sourceId === cash.sourceId),
          types = [...new Set(event?.lines.map((l) => l.sourceType) ?? [])],
          supplier = types.length === 1 && types[0] === "Payable Payment",
          unit = types.length === 1 && types[0] === "Receivable Payment";
        if (matches.length !== 1 || !mapping || (!supplier && !unit) || (unit && (!mapping.accountId || !mapping.periodId || !mapping.projectionPolicy))) {
          setPaymentResults((r) => ({ ...r, [cash.sourceId]: { review: "Review the original payment, bank transaction and invoice allocation mapping first." } }));
          continue;
        }
        const ids = new Set([cash.sourceId, ...cash.applications.map((a) => a.invoiceSourceId)]);
        const history = {
          organisation: packet.history.organisation,
          selector: packet.history.selector,
          buildingId: building,
          accountMappings: packet.history.accountMappings ?? [],
          events: packet.history.events.filter((e) => ids.has(e.sourceId)),
          invoices: packet.history.invoices.filter((i) => ids.has(i.sourceId)),
          payments: [cash],
        };
        const { title: _, ...evidence } = matches[0]!;
        try {
        const command = supplier
          ? { sourceId: cash.sourceId, bankTransactionId: mapping.bankTransactionId, paymentItems: mapping.paymentItems ?? [], history, reason, evidence }
          : {
              sourceId: cash.sourceId,
              bankTransactionId: mapping.bankTransactionId,
              accountId: mapping.accountId,
              periodId: mapping.periodId,
              projectionPolicy: mapping.projectionPolicy,
              ...(mapping.bankWitness?{bankWitness:{...(({checksum,...w})=>w)(mapping.bankWitness),evidence:exactEvidence(mapping.bankWitness.checksum)}}:{}),
              history,
              reason,
              evidence,
            };
          const result = await api<{ canonicalId: string }>(root + (supplier ? "/payable-payments" : "/receivable-payments"), { method: "POST", body: JSON.stringify(command) });
          setPaymentResults((r) => ({ ...r, [cash.sourceId]: result }));
        } catch (e) {
          setPaymentResults((r) => ({ ...r, [cash.sourceId]: { error: e } }));
        }
      }
    } finally {
      setBusy(false);
    }
  }
  const customerCash=packet?.customerCashMappings ?? [],
    overpaymentApplications=customerCash.flatMap(m=>m.originalApplications.map(a=>({...a,overpaymentSourceId:m.sourceId}))),
    customerCashConfirmed=Object.values(customerCashResults).filter(r=>r.canonicalId).length,
    overpaymentConfirmed=Object.values(overpaymentResults).filter(r=>r.canonicalId).length;
  const customerSettlementRows=[
    ...customerCash.map(m=>({id:m.sourceId,date:m.originalDate,kind:m.sourceCreditId?"Customer refund":"Overpayment",amount:m.amount,result:customerCashResults[m.sourceId]})),
    ...overpaymentApplications.map(a=>({id:a.sourceId,date:a.date,kind:"Overpayment allocation",amount:a.amount,result:overpaymentResults[a.sourceId]})),
  ],customerSettlementPages=Math.max(1,Math.ceil(customerSettlementRows.length/25));
  const exactEvidence=(checksum:string)=>originalXeroEvidence(documents,checksum);
  async function applyCustomerCash(){
    if(!packet)return;
    setBusy(true);setError(undefined);stop.current=false;
    try{
      // Refunds can depend on copied overpayments. Original application dates
      // remain independent of import order; native source guards own capacity.
      for(const m of [...customerCash.filter(m=>!m.sourceCreditId),...customerCash.filter(m=>m.sourceCreditId)]){
        if(stop.current)break;
        if(customerCashResults[m.sourceId]?.canonicalId)continue;
        try{
          if(!m.accountId||!m.periodId)throw Error("Prepare the reviewed original property account and accounting period first.");
          const ids=new Set([m.sourceId,m.sourceCreditId,...m.originalApplications.flatMap(a=>[a.sourceId,a.invoiceSourceId])].filter(Boolean));
          const history={organisation:packet.history.organisation,selector:packet.history.selector,buildingId:building,accountMappings:packet.history.accountMappings ?? [],
            events:packet.history.events.filter(e=>ids.has(e.sourceId)),invoices:packet.history.invoices.filter(i=>ids.has(i.sourceId))};
          const {checksum,...witness}=m.bankWitness;
          const result=await api<{canonicalId:string}>(root+"/customer-cash",{method:"POST",body:JSON.stringify({history,sourceId:m.sourceId,bankTransactionId:m.bankTransactionId,accountId:m.accountId,periodId:m.periodId,
            evidence:exactEvidence(m.sourceChecksum),bankWitness:{...witness,evidence:exactEvidence(checksum)},...(m.sourceCreditId?{sourceCreditId:m.sourceCreditId}:{}),
            originalApplications:m.originalApplications.map(({sourceChecksum,...a})=>({...a,evidence:exactEvidence(sourceChecksum)})),reason})});
          setCustomerCashResults(r=>({...r,[m.sourceId]:result}));
        }catch(error){setCustomerCashResults(r=>({...r,[m.sourceId]:{error}}));}
      }
      for(const a of overpaymentApplications){
        if(stop.current)break;
        if(overpaymentResults[a.sourceId]?.canonicalId)continue;
        try{
          const result=await api<{canonicalId:string}>(root+"/overpayment-applications",{method:"POST",body:JSON.stringify({buildingId:building,selector:packet.history.selector,sourceId:a.sourceId,overpaymentSourceId:a.overpaymentSourceId,reason})});
          setOverpaymentResults(r=>({...r,[a.sourceId]:result}));
        }catch(error){setOverpaymentResults(r=>({...r,[a.sourceId]:{error}}));}
      }
    }finally{setBusy(false);}
  }
  const credits = packet?.history.allocations ?? [],
    creditPages = Math.max(1, Math.ceil(credits.length / 25)),
    applied = Object.values(creditResults).filter((r) => r.canonicalId).length;
  const originalEvents=packet?.originalEventMappings??[];
  async function importOriginalEvents(){
    if(!packet||!structureResult)return;setBusy(true);setError(undefined);stop.current=false;
    try{for(const m of originalEvents){
      if(stop.current)break;if(eventResults[m.sourceId]?.canonicalId)continue;
      try{
        const event=packet.history.events.find(e=>e.sourceId===m.sourceId),date=(event?.lines[0] as {date?:string}|undefined)?.date;
        const periods=structureResult.periods.filter(p=>date&&p.startsOn<=date&&p.endsOn>=date);
        if(!event||periods.length!==1)throw Error("Prepare one saved original accounting period for this event.");
        const result=await api<{canonicalId:string}>(root+"/original-events",{method:"POST",body:JSON.stringify({
          history:{organisation:packet.history.organisation,selector:packet.history.selector,buildingId:building,events:[event],invoices:[],accountMappings:packet.history.accountMappings??[]},
          sourceId:m.sourceId,scheduleId:structureResult.scheduleId,periodId:periods[0]!.id,evidence:exactEvidence(m.sourceChecksum),lineMappings:m.lineMappings,
          sourceOnlyBanks:(m.sourceOnlyBanks??[]).map(b=>({...b,evidence:exactEvidence(b.historyChecksum)})),
          bankLinks:m.bankLinks.map(({witness,...l})=>{const {checksum,...source}=witness;return {...l,witness:{...source,evidence:exactEvidence(checksum)}}}),
          fundProjection:"Retain original nominal funds with explicit transfer controls",reason})});
        setEventResults(r=>({...r,[m.sourceId]:result}));
      }catch(error){setEventResults(r=>({...r,[m.sourceId]:{error}}));}
    }}finally{setBusy(false);}
  }
  const reference = (id: string) => packet?.history.invoices.find((i) => i.sourceId === id)?.reference || "Original reference not supplied";
  async function applyCredits() {
    if (!packet) return;
    setBusy(true);
    setError(undefined);
    stop.current = false;
    try {
      for (const allocation of credits) {
        if (stop.current) break;
        if (creditResults[allocation.sourceId]?.canonicalId) continue;
        const ids = new Set([allocation.sourceId, allocation.creditSourceId, allocation.debitSourceId]);
        try {
          const result = await api<{ canonicalId: string }>(root + "/credit-applications", {
            method: "POST",
            body: JSON.stringify({
              history: {
                organisation: packet.history.organisation,
                selector: packet.history.selector,
                buildingId: building,
                events: packet.history.events.filter((e) => ids.has(e.sourceId)),
                invoices: packet.history.invoices.filter((i) => ids.has(i.sourceId)),
                allocations: [allocation],
              },
              sourceId: allocation.sourceId,
              reason,
            }),
          });
          setCreditResults((r) => ({ ...r, [allocation.sourceId]: result }));
        } catch (e) {
          setCreditResults((r) => ({ ...r, [allocation.sourceId]: { error: e } }));
        }
      }
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="administration-workspace">
      <Link to="/admin">Administration</Link>
      <PageHeader title="Historic Xero import"/>
      <p>Import retained invoices and credits for this managed building. MY Facilities Management remains on Xero and is excluded from this tool.</p>
      <p>Original paid amounts support settlement mapping. Prepared invoices await coding and approval in the supplier register.</p>
      <label>
        Verified history preparation file{" "}
        <input
          type="file"
          accept=".json,application/json"
          disabled={busy}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void load(f);
          }}
        />
      </label>
      {error != null && <ErrorState error={error} />}
      {packet && (
        <>
          {packet.structureMapping && (
            <section className="panel">
              <h2>Original ledger accounts and periods</h2>
              <p>
                {packet.structureMapping.units.length} actual units · {packet.structureMapping.otherAccounts.length} original non-unit accounts ·{" "}
                {packet.structureMapping.periods.length} complete accounting periods.
              </p>
              <p>The original ledger retains its own accounts and dates. Lease ownership and apportionments remain subject to their separate evidence review.</p>
              <button disabled={busy} onClick={() => void prepareStructure()}>
                Prepare original accounts and periods
              </button>
              {structureResult && (
                <p role="status">
                  Prepared {structureResult.accounts.length} accounts and {structureResult.periods.length} periods. Invoice mappings now use these saved records.
                </p>
              )}
            </section>
          )}
          {!!packet.cashGroupMappings?.length&&<XeroCashGroups packet={packet} structure={structureResult} evidence={exactEvidence} reason={reason} root={root} busy={busy} setBusy={setBusy} stop={stop}/>}
          {!!packet.supplierCashMappings?.length&&<XeroSupplierCash packet={packet} structure={structureResult} evidence={exactEvidence} reason={reason} root={root} busy={busy} setBusy={setBusy} stop={stop}/>}
          {!!packet.supplierSourceCashMappings?.length&&<XeroSupplierSourceCash packet={packet} structure={structureResult} evidence={exactEvidence} reason={reason} root={root} busy={busy} setBusy={setBusy} stop={stop}/>}
          {!!packet.payableNettingMappings?.length&&<XeroPayableNetting packet={packet} structure={structureResult} evidence={exactEvidence} reason={reason} root={root} busy={busy} setBusy={setBusy} stop={stop}/>}
          <h2>Supplier invoices and credits</h2>
          <p>
            {invoices.length} active original records. {prepared} prepared in this session.
          </p>
          <label>
            Import reason <input value={reason} disabled={busy} onChange={(e) => setReason(e.target.value)} />
          </label>
          <button disabled={busy || reason.trim().length < 5 || !invoices.length} onClick={() => void prepare()}>
            Prepare invoices and credits for review
          </button>
          <p>Copying retained source accounting preserves the original supplier label and leaves unrecorded approvals unknown. Current invoice approval and payment authority use their existing controls.</p>
          <button disabled={busy || reason.trim().length<5 || !structureResult || !invoices.some(i=>payableMappingFor(i.sourceId))} onClick={()=>void postPayables(true)}>
            Copy original supplier accounting
          </button>
          <button disabled={busy || reason.trim().length < 5 || !invoices.some((i) => payableMappingFor(i.sourceId))} onClick={() => void postPayables()}>
            Post approved original supplier accounting
          </button>
          {busy && (
            <button
              onClick={() => {
                stop.current = true;
              }}
            >
              Stop after current record
            </button>
          )}
          <p role="status">{busy ? "Import in progress. Each confirmed invoice is retained separately." : "Prepared records can be reviewed in the supplier invoice register."}</p>
          {originalEvents.length>0&&<section aria-label="Original bank entries and journals">
            <h2>Original bank entries and journals</h2>
            <p>{originalEvents.length} original records · {Object.values(eventResults).filter(r=>r.canonicalId).length} confirmed in this session.</p>
            <p>Original dates and nominal balances are retained. Fund attribution controls are labelled separately. Conversion bank balances retain their source labels and await opening-balance review.</p>
            <button disabled={busy||!structureResult||reason.trim().length<5} onClick={()=>void importOriginalEvents()}>Copy original bank entries and journals</button>
<ResponsiveTable><thead><tr><th>Original record</th><th>Import result</th></tr></thead><tbody>{originalEvents.slice(eventPage*25,(eventPage+1)*25).map(m=><tr key={m.sourceId}><td>{m.sourceId}</td><td>{eventResults[m.sourceId]?.canonicalId?"Copied":eventResults[m.sourceId]?.error?<ErrorState error={eventResults[m.sourceId]?.error}/>:"Ready for source checks"}</td></tr>)}</tbody></ResponsiveTable>
            {originalEvents.length>25&&<nav aria-label="Original bank and journal import pages"><button disabled={eventPage===0} onClick={()=>setEventPage(eventPage-1)}>Previous</button> Page {eventPage+1} of {Math.ceil(originalEvents.length/25)} <button disabled={(eventPage+1)*25>=originalEvents.length} onClick={()=>setEventPage(eventPage+1)}>Next</button></nav>}
          </section>}
<ResponsiveTable>
            <thead>
              <tr>
                <th>Date</th>
                <th>Reference</th>
                <th>Supplier in Xero</th>
                <th>Kind</th>
                <th>Original total</th>
                <th>Import result</th>
                <th>Original accounting</th>
              </tr>
            </thead>
            <tbody>
              {invoices.slice(page * 25, (page + 1) * 25).map((i) => (
                <tr key={i.sourceId}>
                  <td>{i.date}</td>
                  <td>{i.reference || "Not supplied"}</td>
                  <td>{i.contact}</td>
                  <td>{i.kind}</td>
                  <td>£{i.total}</td>
                  <td>
                    {results[i.sourceId]?.canonicalId ? (
                      <Link to={`/finance/payables?invoice=${results[i.sourceId]!.canonicalId}`}>Open prepared invoice</Link>
                    ) : results[i.sourceId]?.review ? (
                      results[i.sourceId]!.review
                    ) : results[i.sourceId]?.error ? (
                      <ErrorState error={results[i.sourceId]!.error} />
                    ) : evidenceFor(i.sourceId).length === 1 ? (
                      "Source retained; ready to prepare"
                    ) : (
                      "Source evidence needs review"
                    )}
                  </td>
                  <td>
                    {payableResults[i.sourceId]?.canonicalId ? (
                      "Original accounting posted"
                    ) : payableResults[i.sourceId]?.review ? (
                      payableResults[i.sourceId]!.review
                    ) : payableResults[i.sourceId]?.error ? (
                      <ErrorState error={payableResults[i.sourceId]!.error} />
                    ) : payableMappingFor(i.sourceId) ? (
                      "Reviewed original accounting mapping supplied"
                    ) : (
                      "Original accounting mapping needed"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </ResponsiveTable>
          {pages > 1 && (
            <nav aria-label="Invoice import pages">
              <button disabled={page === 0} onClick={() => setPage(page - 1)}>
                Previous
              </button>{" "}
              Page {page + 1} of {pages}{" "}
              <button disabled={page + 1 >= pages} onClick={() => setPage(page + 1)}>
                Next
              </button>
            </nav>
          )}
          <h2>Original customer invoices and credits</h2>
          <p>
            {receivables.length} active original records. {postedReceivables} posted in this session. Reviewed mappings must identify the customer account, original period and each
            accounting line. Importing these records does not generate or send new demands or verify historical legal ownership.
          </p>
          <button
            disabled={busy || reason.trim().length < 5 || !receivables.some((i) => mappingFor(i.sourceId) && evidenceFor(i.sourceId).length === 1)}
            onClick={() => void importReceivables()}
          >
            Import reviewed customer invoices and credits
          </button>
<ResponsiveTable>
            <thead>
              <tr>
                <th>Date</th>
                <th>Original reference</th>
                <th>Original addressee</th>
                <th>Kind</th>
                <th>Original total</th>
                <th>Import result</th>
              </tr>
            </thead>
            <tbody>
              {receivables.slice(receivablePage * 25, (receivablePage + 1) * 25).map((i) => (
                <tr key={i.sourceId}>
                  <td>{i.date}</td>
                  <td>{i.reference || "Not supplied"}</td>
                  <td>{i.contact}</td>
                  <td>{i.kind}</td>
                  <td>£{i.total}</td>
                  <td>
                    {receivableResults[i.sourceId]?.canonicalId ? (
                      "Original invoice and accounting lines posted"
                    ) : receivableResults[i.sourceId]?.review ? (
                      receivableResults[i.sourceId]!.review
                    ) : receivableResults[i.sourceId]?.error ? (
                      <ErrorState error={receivableResults[i.sourceId]!.error} />
                    ) : mappingFor(i.sourceId) && evidenceFor(i.sourceId).length === 1 ? (
                      "Reviewed mapping supplied"
                    ) : (
                      "Account, period and accounting mapping needed"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </ResponsiveTable>
          {receivablePages > 1 && (
            <nav aria-label="Customer invoice import pages">
              <button disabled={receivablePage === 0} onClick={() => setReceivablePage(receivablePage - 1)}>
                Previous
              </button>{" "}
              Page {receivablePage + 1} of {receivablePages}{" "}
              <button disabled={receivablePage + 1 >= receivablePages} onClick={() => setReceivablePage(receivablePage + 1)}>
                Next
              </button>
            </nav>
          )}
          <h2>Original payment allocations</h2>
          <p>
            {payments.length} retained payments. {Object.values(paymentResults).filter((r) => r.canonicalId).length} confirmed in this session. This attributes existing bank
            transactions; it does not send money. Shared bank transactions retain their separate original payment legs. Derived bookkeeping allocation dates are labelled separately
            from original cash dates.
          </p>
          <button
            disabled={busy || reason.trim().length < 5 || !payments.some((p) => paymentMappingFor(p.sourceId) && paymentEvidenceFor(p.sourceId).length === 1)}
            onClick={() => void applyPayments()}
          >
            Import reviewed payment allocations
          </button>
<ResponsiveTable>
            <thead>
              <tr>
                <th>Original cash date</th>
                <th>Invoice references</th>
                <th>Amount</th>
                <th>Import result</th>
              </tr>
            </thead>
            <tbody>
              {payments.slice(paymentPage * 25, (paymentPage + 1) * 25).map((p) => (
                <tr key={p.sourceId}>
                  <td>{p.date}</td>
                  <td>{p.applications.map((a) => reference(a.invoiceSourceId)).join(", ")}</td>
                  <td>£{p.amount}</td>
                  <td>
                    {paymentResults[p.sourceId]?.canonicalId ? (
                      "Original cash and invoice allocation retained"
                    ) : paymentResults[p.sourceId]?.review ? (
                      paymentResults[p.sourceId]!.review
                    ) : paymentResults[p.sourceId]?.error ? (
                      <ErrorState error={paymentResults[p.sourceId]!.error} />
                    ) : paymentMappingFor(p.sourceId) ? (
                      "Reviewed bank mapping supplied"
                    ) : (
                      "Original bank and invoice mapping needed"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </ResponsiveTable>
          {paymentPages > 1 && (
            <nav aria-label="Payment import pages">
              <button disabled={paymentPage === 0} onClick={() => setPaymentPage(paymentPage - 1)}>
                Previous
              </button>{" "}
              Page {paymentPage + 1} of {paymentPages}{" "}
              <button disabled={paymentPage + 1 >= paymentPages} onClick={() => setPaymentPage(paymentPage + 1)}>
                Next
              </button>
            </nav>
          )}
          {customerCash.length>0 && <section aria-label="Original overpayments and refunds">
            <h2>Original overpayments and refunds</h2>
            <p>{customerCash.length} original cash components · {customerCashConfirmed} confirmed in this session. {overpaymentApplications.length} original overpayment allocations · {overpaymentConfirmed} confirmed in this session. Original accounting and bank dates are retained separately. These copies use existing bank movements and send no money.</p>
            <button disabled={busy||reason.trim().length<5} onClick={()=>void applyCustomerCash()}>Import original overpayments, refunds and allocations</button>
<ResponsiveTable>
              <thead><tr><th>Original date</th><th>Kind</th><th>Amount</th><th>Import result</th></tr></thead>
              <tbody>{customerSettlementRows.slice(customerCashPage*25,(customerCashPage+1)*25).map(r=><tr key={r.id}>
                <td>{r.date}</td><td>{r.kind}</td><td>£{r.amount}</td><td>{r.result?.canonicalId?"Original source and allocation retained":r.result?.error?<ErrorState error={r.result.error}/>:"Reviewed source mapping supplied"}</td>
              </tr>)}</tbody>
            </ResponsiveTable>
            {customerSettlementPages>1&&<nav aria-label="Original cash settlement pages">
              <button disabled={customerCashPage===0} onClick={()=>setCustomerCashPage(customerCashPage-1)}>Previous</button>{" "}Page {customerCashPage+1} of {customerSettlementPages}{" "}
              <button disabled={customerCashPage+1>=customerSettlementPages} onClick={()=>setCustomerCashPage(customerCashPage+1)}>Next</button>
            </nav>}
          </section>}
          <h2>Original credit applications</h2>
          <p>
            {credits.length} retained relationships. {applied} confirmed in this session. Both the original invoice and credit must already be reviewed and posted before their
            credit can be applied. Each application keeps its original effective date.
          </p>
          <button disabled={busy || reason.trim().length < 5 || !credits.length} onClick={() => void applyCredits()}>
            Apply original credits to posted invoices
          </button>
<ResponsiveTable>
            <thead>
              <tr>
                <th>Effective date</th>
                <th>Credit reference</th>
                <th>Invoice reference</th>
                <th>Amount</th>
                <th>Import result</th>
              </tr>
            </thead>
            <tbody>
              {credits.slice(creditPage * 25, (creditPage + 1) * 25).map((a) => (
                <tr key={a.sourceId}>
                  <td>{a.effectiveDate}</td>
                  <td>{reference(a.creditSourceId)}</td>
                  <td>{reference(a.debitSourceId)}</td>
                  <td>£{a.amount}</td>
                  <td>
                    {creditResults[a.sourceId]?.canonicalId ? (
                      "Original application retained"
                    ) : creditResults[a.sourceId]?.error ? (
                      <ErrorState error={creditResults[a.sourceId]!.error} />
                    ) : (
                      "Awaiting original posted invoice and credit"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </ResponsiveTable>
          {creditPages > 1 && (
            <nav aria-label="Credit application import pages">
              <button disabled={creditPage === 0} onClick={() => setCreditPage(creditPage - 1)}>
                Previous
              </button>{" "}
              Page {creditPage + 1} of {creditPages}{" "}
              <button disabled={creditPage + 1 >= creditPages} onClick={() => setCreditPage(creditPage + 1)}>
                Next
              </button>
            </nav>
          )}
        </>
      )}
    </div>
  );
}
